import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { FeatureName } from '@treams/treams-network';
import { Button, FlexGap, Paragraph, Pill, QuillText, Subheading, tokens } from '@treams/treams-ui';

import { GrowthPathData } from '../GrowthPathConverter';
import { useGrowthPathJobCompetencyFilter } from '../hooks/useGrowthPathFilter';
import { GrowthPathJobCompetency } from '../types';
import GrowthPathCompetencyLevel from './GrowthPathCompetencyLevel';

const CompetencyContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.group}px;
	padding: ${tokens.padding.section}px 0;
`;

const Levels = styled.div`
	border-top: 1px solid ${tokens.border.subtle};
	border-bottom: 1px solid ${tokens.border.subtle};
`;

type Props = {
	data: GrowthPathData;
	labelID: string | null;
};

const GrowthPathCompetenciesList: FC<Props> = ({ data, labelID }) => {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const location = useLocation();
	const { appraisals, jobCompetencies } = data;
	const showViewScoresButton = useFeature(FeatureName.CompetencyDetailScreen);

	const minLevels = new Map<string, number>();

	const filteredJobCompetencies = useGrowthPathJobCompetencyFilter(labelID, jobCompetencies) ?? [];
	for (const competency of filteredJobCompetencies) {
		minLevels.set(
			competency.competency.id,
			Math.max(competency.competencyLevel, minLevels.get(competency.id) ?? 0),
		);
	}

	const getAppraisal = (jobCompetency: GrowthPathJobCompetency) => {
		return appraisals.find((appraisal) => appraisal.competency.id === jobCompetency.competency.id);
	};

	const getCompetencyLink = (competencyID: string) => {
		return `${location.pathname}/competency/${competencyID}`;
	};

	return (
		<>
			{filteredJobCompetencies!.map((jobCompetency, i) => {
				const description = localize(jobCompetency.competency.description!).text;
				const hasDescription = !QuillText.isEmpty(description);

				return (
					<CompetencyContainer key={i}>
						<FlexGap flexDirection="column" gap={tokens.gap.inline}>
							<Subheading mb={0}>{localize(jobCompetency.competency.title!).text}</Subheading>
							{hasDescription && (
								<Paragraph small mb={0}>
									<QuillText value={description} />
								</Paragraph>
							)}
						</FlexGap>

						{(showViewScoresButton || !getAppraisal(jobCompetency)) && (
							<FlexGap gap={tokens.gap.list} alignItems="center" flexWrap="wrap">
								{!getAppraisal(jobCompetency) && (
									<Pill flexShrink={0}>{t('growthPath.competency.noScore')}</Pill>
								)}
								{showViewScoresButton && (
									<Button
										title={t('growthPath.competency.viewScores')}
										type="secondary"
										small
										link={getCompetencyLink(jobCompetency.competency.id)}
									/>
								)}
							</FlexGap>
						)}

						<Levels>
							{jobCompetency.competency.levels.map((level, i) => (
								<GrowthPathCompetencyLevel
									key={i}
									index={i}
									appraisal={getAppraisal(jobCompetency)}
									level={level}
									min={minLevels.get(jobCompetency.competency.id!) ?? 0}
								/>
							))}
						</Levels>
					</CompetencyContainer>
				);
			})}
		</>
	);
};
export default GrowthPathCompetenciesList;
